/**
 * Pure helpers for classifying post-sign-in profile-load failures, computing the
 * retry delay used by the login screen, and mapping `LoginResult` failure variants
 * to user-facing toast copy — no React, no Amplify, no atoms.
 *
 * Kept in `util/` (rather than colocated with `app/(open)/login.tsx`) so it stays out of
 * the Expo Router routes folder and so unit tests can import it without dragging in the
 * full login-screen module graph (Amplify side effects, providers, atoms).
 */

import { SUPPORT_EMAIL_ADDRESS } from '@/constants/support';
import { ONE_SECOND } from '@/constants/time';

export type ResolveFailure = 'transient' | 'persistent';

export type ResolveResult = { ok: true } | { ok: false; failure: ResolveFailure };

/**
 * Classify a `getCurrentUserV2` failure as transient (worth retrying) or persistent
 * (the session is in a state we cannot recover from automatically).
 *
 * Transient: 5xx, 429 rate limits, or any non-HTTP error (network/timeout/abort).
 * Persistent: any other 4xx (404 user-deleted, 401 token mismatch, 403, etc.).
 */
export function classifyResolveError(error: unknown): ResolveFailure {
  const status =
    typeof error === 'object' && error !== null && 'status' in error
      ? (error as { status?: unknown }).status
      : undefined;

  if (typeof status !== 'number') {
    return 'transient';
  }

  if (status >= 500 || status === 429) {
    return 'transient';
  }

  return 'persistent';
}

export const RESOLVE_RETRY_DELAY_MS = 1.5 * ONE_SECOND;

/**
 * Variants of `LoginResult` that should produce a user-facing failure toast. Kept
 * narrow to the failure shapes so callers don't accidentally pass `'success'` or
 * `'newPasswordRequired'` (which have non-toast handling).
 */
export type LoginFailureVariant = 'transientFailure' | 'persistentFailure' | 'unsupportedStep';

/**
 * Map a `LoginResult` failure variant to user-facing toast copy. Centralizes the
 * messages so login.tsx, authModal.tsx, and new-password.tsx stay in sync.
 */
export function getLoginFailureToast(variant: LoginFailureVariant): {
  title: string;
  message: string;
} {
  if (variant === 'transientFailure') {
    return {
      title: 'We could not load your profile right now',
      message: 'Please check your connection and try signing in again.',
    };
  }

  if (variant === 'persistentFailure') {
    return {
      title: 'We could not finish signing you in',
      message: `Your account is in a state we could not recover from. Please contact support at ${SUPPORT_EMAIL_ADDRESS}.`,
    };
  }

  return {
    title: 'Additional verification is required to sign in',
    message: `Please contact support at ${SUPPORT_EMAIL_ADDRESS} so we can help you finish signing in.`,
  };
}
